<script setup>
import { useAppStore } from '@/stores/App'
import { useDisplay } from 'vuetify'

const nuxtApp = useNuxtApp()
const app = useAppStore()

const { xs, smAndDown, mdAndUp } = useDisplay()
const staticURLImg = useRuntimeConfig().public.staticURLImg
const { getDaysLeftNumber } = useFormatters()

const searchText = ref()
const selectedCategory = ref()
const selectedSort = ref()

const { data, status, error } = await useLazyAsyncData('deals', () => $fetch('/api/deal/'), {
    transform: (data) => {
        return {
            deals: data.deals.edges.map((edge) => edge.node),
            fetchedAt: new Date(Date.now()),
        }
    },
    getCachedData(key) {
        const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key]

        if (!data) {
            return
        }

        const expirationDate = new Date(data.fetchedAt)
        expirationDate.setTime(expirationDate.getTime() + 60 * 60000) // cache set to 1 hour
        const isExpired = expirationDate.getTime() < Date.now()
        if (isExpired) {
            return
        }

        return data
    },
})

watchEffect(() => {
    if (error.value) {
        throw createError({
            statusCode: 500,
            message: error.value,
            fatal: false,
        })
    }
})

// console.log('data ', data.value.deals)

// Category list for filtering
const categories = computed(() => {
    return [...new Set(data.value.deals.map((value) => value.category.name))]
})

// Filter deals by search text, category or sort optios
const deals = computed(() => {
    let nodes = data.value.deals
    let tempArr = nodes

    // Filter by selected category
    if (selectedCategory.value != null) {
        tempArr = nodes.filter((e) => {
            return e.category.name.toLowerCase() === selectedCategory.value.toLowerCase()
        })
    }

    // Filter by search text
    if (searchText.value != null && searchText.value.length > 0) {
        tempArr = nodes.filter((e) => {
            return (e.brand.name + '' + e.title + '' + e.content + '' + e.keywords + '' + e.description)
                .toLowerCase()
                .includes(searchText.value.toLowerCase())
        })
    }

    // Filter by selected dropdown option
    if (selectedSort.value != null) {
        switch (selectedSort.value) {
            case 'Brand: A to Z':
                tempArr.sort(function (a, b) {
                    if (a.brand.name < b.brand.name) {
                        return -1
                    }
                    if (a.brand.name > b.brand.name) {
                        return 1
                    }
                    return 0
                })
                break
            case 'Brand: Z to A':
                tempArr.sort(function (a, b) {
                    if (a.brand.name < b.brand.name) {
                        return 1
                    }
                    if (a.brand.name > b.brand.name) {
                        return -1
                    }
                    return 0
                })
                //.reverse()
                break
            case 'Price: Low to High':
                tempArr.sort(function (a, b) {
                    return a.salePrice - b.salePrice
                })
                break
            case 'Price: High to Low':
                tempArr.sort(function (a, b) {
                    return b.salePrice - a.salePrice
                })
                break
            case 'DaysLeft: Low to High':
                tempArr.sort(function (a, b) {
                    return getDaysLeftNumber(a.endsAt) - getDaysLeftNumber(b.endsAt)
                })
                break
            case 'DaysLeft: High to Low':
                tempArr.sort(function (a, b) {
                    return getDaysLeftNumber(b.endsAt) - getDaysLeftNumber(a.endsAt)
                })
                break
            default:
                break
        }
    }
    return tempArr
})

useSeoMeta({
    title: 'Deals',
    ogTitle: 'Deals',
    description: 'Explore deals on music production and graphic design software products.',
    ogDescription: 'Explore deals on music production and graphic design software products.',
})
</script>

<template>
    <v-row id="deals" no-gutters class="mt-4 justify-center slide-from-top align-stretch">
        <v-col v-if="status === 'pending' || status === 'idle'" cols="10"><app-loader /></v-col>
        <v-col v-else="deals" cols="12" sm="10" class="ma-0 pa-0">
            <v-row>
                <v-col cols="12" md="8">
                    <lazy-ad-carousel spot="carousel" page="deals" position="top" class="slide-from-left" />
                </v-col>
                <v-col cols="12" md="4" class="d-flex flex-column">
                    <lazy-ad-sidebar
                        spot="sidebar"
                        page="deals"
                        :placing="mdAndUp ? 6 : 6"
                        position="top"
                        class="slide-from-right"
                    />
                </v-col>
            </v-row>
            <!-- <v-row class="">
                <v-col>
                    <v-divider></v-divider>
                </v-col>
            </v-row> -->
            <v-row justify="end" align="start" class="mt-8 mb-2 rounded">
                <v-col cols="12" sm="12" lg="4">
                    <app-field-search
                        v-model="searchText"
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="mdi-magnify"
                        placeholder="Start typing to search..."
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" sm="6" lg="4">
                    <app-field-select
                        v-model="selectedCategory"
                        :items="categories"
                        variant="outlined"
                        label="Filter..."
                        density="comfortable"
                        prepend-inner-icon="mdi-filter-variant"
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" sm="6" lg="4">
                    <app-field-select
                        v-model="selectedSort"
                        :items="app.dealsSortOptions"
                        variant="outlined"
                        label="Sort..."
                        density="comfortable"
                        prepend-inner-icon="mdi-sort"
                        hide-details="auto"
                    />
                </v-col>
            </v-row>
            <v-row class="justify-center">
                <v-col v-for="deal in deals" :key="deal.id" cols="12" sm="6" md="4" lg="3">
                    <deal-card :deal="deal" />
                </v-col>
            </v-row>
        </v-col>
        <v-col cols="12" sm="10">
            <v-row class="my-16 mx-auto justify-center">
                <v-col
                    cols="12"
                    sm="8"
                    md="6"
                    lg="4"
                    class="text-h6 text-center bg-secondary"
                    :class="xs ? 'rounded-t' : 'rounded-s'"
                >
                    Want to add your deals for free?
                </v-col>
                <v-col
                    cols="12"
                    sm="4"
                    md="4"
                    lg="2"
                    class="text-center bg-secondary"
                    :class="xs ? 'rounded-b' : 'rounded-e'"
                >
                    <app-button
                        :to="`support`"
                        :btn-text="`Contact Us`"
                        :append-icon="`mdi-chevron-right`"
                        size="default"
                        aria-label="Contact Us"
                    />
                </v-col>
            </v-row>
            <ad-disclosure />
        </v-col>
    </v-row>
</template>

<style scoped></style>
